import { ReactElement, useMemo } from "react";
import { AnnouncementFormModel } from "./EditAnnouncementDialog";
import { MediaContentModel } from "client/Features/Hub/HubDetailsLayout";
import { TagModel } from "client/Components";
import { isNil } from "client/Misc/utils";
import { TimeZone } from "client/DateAndTime/timezones";
import { HubItemCategoryModel } from "client/Api/Hub/hub";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import {
	ExistingHubItemImages,
	HubHierarchyListCategoryNodeModel,
	HubHierarchyListNodeModel
} from "client/Api/Hub/common.composer";
import {
	convertLayoutForDetails,
	convertMediaForDetails,
	convertTagForDetails
} from "client/Features/Hub/converters";
import { ContentWithRelevant } from "client/Features/Hub/ContentWithRelevant";
import { ContentViewerEmptyBlockSettings, HubItemLayoutContentViewerModel } from "client/Features/Hub/Builder/Viewer/ContentViewer";
import { LinkedDocumentPreviewModel } from "client/Features/Hub/LinkedDocumentsPreview";
import { AnnouncementContent } from "client/Features/Hub/Announcements/Details/AnnouncementContent";
import { getLinkedDocumentId } from "client/Features/Hub/useAddDocuments";
import { getDocumentKind } from "client_legacy/Documents/fileExtensions";
import { DocumentItemType } from "client/Api/Documents/SharePoint/common";
import { localize } from "client_legacy/ndaw/culture";
import { getComputedDisplayName } from "client/Features/AddressBook/common";
import { HubItemNameOnlyCategory } from "client/Features/Hub/Content/common";
import { UserIdentityModel } from "client/Api/UserProfile/service";
import { IValueValidation } from "client/Validation/input";
import { findHubCategoryItemNodes } from "../../common";

export type PreviewProps = {
	model: AnnouncementFormModel;
	validation: IValueValidation<AnnouncementFormModel>;
	createdBy: UserIdentityModel | null;
	hasStartTime: boolean;
	timezone: TimeZone;
	categoryHierarchy: readonly HubHierarchyListNodeModel[];
	originalImages: ExistingHubItemImages;
};

export const Preview = (props: PreviewProps): ReactElement => {
	const {
		model,
		validation,
		createdBy,
		hasStartTime,
		timezone,
		categoryHierarchy,
		originalImages
	} = props;

	const startTime = useMemo(
		(): Date => {
			if (!hasStartTime || !model.startTime) {
				return new Date();
			}

			return model.startTime;
		},
		[hasStartTime, model.startTime]
	);

	const computedCategories = useMemo(
		(): readonly HubItemCategoryModel[] | [HubItemNameOnlyCategory] => {
			if (model.categoryIds.length === 0) {
				return [
					{
						name: localize("_CategoryName")
					}
				];
			}

			const result: readonly HubHierarchyListCategoryNodeModel[] = findHubCategoryItemNodes(
				categoryHierarchy,
				model.categoryIds
			);

			return result.map(
				x => x.category
			);
		},
		[
			categoryHierarchy,
			model.categoryIds
		]
	);

	const media = useMemo(
		(): MediaContentModel => {
			return convertMediaForDetails(
				model.media,
				HubItemKind.Announcement,
				originalImages.coverImage
			);
		},
		[model.media, originalImages.coverImage]
	);

	const layout = useMemo(
		(): HubItemLayoutContentViewerModel => {
			return convertLayoutForDetails(model.layout, originalImages.layoutImages);
		},
		[model.layout, originalImages.layoutImages]
	);

	const tags = useMemo(
		(): TagModel<number>[] => {
			return model.tags
				.map(convertTagForDetails);
		},
		[model.tags]
	);

	const linkedDocuments = useMemo(
		(): readonly LinkedDocumentPreviewModel[] => {
			return model.linkedDocuments
				.map(x => ({
					id: getLinkedDocumentId(x),
					name: x.name,
					thumbnailKind: getDocumentKind(
						DocumentItemType.File,
						x.extension
					)
				}));
		},
		[model.linkedDocuments]
	);

	const {
		authorId,
		authorName
	} = useMemo(
		(): { authorId: number | null; authorName: string | null; } => {
			if (!createdBy || !model.shouldShowAuthor) {
				return {
					authorId: null,
					authorName: null
				};
			}

			const {
				userId,
				firstName,
				lastName
			} = createdBy;

			const {
				computedDisplayName
			} = getComputedDisplayName({
				firstName: firstName,
				lastName: lastName
			});

			return {
				authorId: userId,
				authorName: computedDisplayName
			};
		},
		[createdBy, model.shouldShowAuthor]
	);

	return (
		<ContentWithRelevant>
			<ContentWithRelevant.Content>
				<AnnouncementContent
					hasData
					title={model.title
						? model.title
						: {
							kind: "placeholder",
							value: localize("_EnterTitle")
						}
					}
					media={media}
					layout={layout}
					emptyBlockSettings={emptyBlockSettings}
					categories={computedCategories}
					areCommentsEnabled={false}
					authorId={authorId}
					authorName={authorName}
					isEditable={false}
					isPinned={model.isPinned}
					publicationDate={isNil(startTime) || validation.for("startTime").isValid()
						? startTime
						: INVALID_DATE
					}
					tags={tags}
					linkedDocumentsMode="preview"
					linkedDocuments={linkedDocuments}
					mode="details-only"
					hasStatistics={false}
					timezone={timezone}
					shouldNotRenderEmptyFooter
				/>
			</ContentWithRelevant.Content>
		</ContentWithRelevant>
	);
};
Preview.displayName = "Preview";

const emptyBlockSettings: ContentViewerEmptyBlockSettings = {
	faqElement: {
		question: {
			kind: "placeholder",
			placeholder: localize("_EnterQuestion")
		},
		answer: {
			kind: "placeholder",
			placeholder: localize("_EnterAnswer")
		}
	}
};
const INVALID_DATE: Date = new Date("");